import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { changePassword as changePasswordApi, currentSession, deleteAccount as deleteAccountApi, requestPasswordReset, resetPassword as resetPasswordApi, signIn as signInApi, signOut as signOutApi, signUp as signUpApi, SignUpInput, updateProfile as updateProfileApi, UserProfile } from '../lib/auth';
import { supabase } from '../lib/supabaseClient';
import { dropUserData } from '../lib/storage';
interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signUp: (input: SignUpInput) => Promise<{
    error?: string;
  }>;
  signIn: (email: string, password: string, remember: boolean) => Promise<{
    error?: string;
  }>;
  signOut: () => Promise<void>;
  requestReset: (email: string) => Promise<{
    ok?: boolean;
    error?: string;
  }>;
  resetPassword: (password: string, confirmPassword: string) => Promise<{
    ok?: boolean;
    error?: string;
  }>;
  updateProfile: (patch: Partial<Omit<UserProfile, 'id' | 'createdAt'>>) => Promise<{
    error?: string;
  }>;
  changePassword: (current: string, password: string, confirmPassword: string) => Promise<{
    ok?: boolean;
    error?: string;
  }>;
  deleteAccount: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  useEffect(() => {
    let active = true;
    currentSession().then(profile => {
      if (active) {
        setUser(profile);
        setLoading(false);
      }
    });
    const {
      data: sub
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        if (active) setPasswordRecovery(true);
      }
      if (!session?.user) {
        if (active) setUser(null);
        return;
      }
      currentSession().then(profile => {
        if (active) setUser(profile);
      });
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  const clearPasswordRecovery = useCallback(() => setPasswordRecovery(false), []);
  const signUp = useCallback(async (input: SignUpInput) => {
    const result = await signUpApi(input);
    if (result.user) setUser(result.user);
    return {
      error: result.error
    };
  }, []);
  const signIn = useCallback(async (email: string, password: string, remember: boolean) => {
    const result = await signInApi(email, password, remember);
    if (result.user) setUser(result.user);
    return {
      error: result.error
    };
  }, []);
  const signOut = useCallback(async () => {
    await signOutApi();
    setUser(null);
  }, []);
  const updateProfile = useCallback(async (patch: Partial<Omit<UserProfile, 'id' | 'createdAt'>>) => {
    if (!user) return {
      error: 'No hay sesión activa.'
    };
    const result = await updateProfileApi(user.id, patch);
    if (result.user) setUser(result.user);
    return {
      error: result.error
    };
  }, [user]);
  const changePassword = useCallback(async (current: string, password: string, confirmPassword: string) => {
    if (!user) return {
      error: 'No hay sesión activa.'
    };
    return changePasswordApi(user.id, current, password, confirmPassword);
  }, [user]);
  const deleteAccount = useCallback(async () => {
    if (!user) return;
    dropUserData(user.id);
    await deleteAccountApi(user.id);
    setUser(null);
  }, [user]);
  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    passwordRecovery,
    clearPasswordRecovery,
    signUp,
    signIn,
    signOut,
    requestReset: requestPasswordReset,
    resetPassword: resetPasswordApi,
    updateProfile,
    changePassword,
    deleteAccount
  }), [user, loading, passwordRecovery, clearPasswordRecovery, signUp, signIn, signOut, updateProfile, changePassword, deleteAccount]);
  return <AuthContext.Provider value={value} data-id="element-589" data-mp="2jo2iudizgmdl">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}